/****************************************************
 * Module Name:  stoneSearchHistory.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useEffect } from "react";
import Select from "react-select";
import Constants from "../../utility/constants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LoadingButton } from "@mui/lab";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";

import {
  Box,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGridPro, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import Utility from "../../utility/utility";
import SavedsearchhistoryProxy from "../../utility/proxy/manager/savedsearchhistoryProxy";
import pdfFonts from "pdfmake/build/vfs_fonts";
import pdfMake from "pdfmake/build/pdfmake";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import renderCellExpand from "../common/dataGridCellExpand";
import NoPermission from "../common/noPermission";
import CommonSnackbar from "../common/commonSnackBar";
import dayjs from "dayjs";
import PageTitle from "../common/pageTitle";

pdfMake.vfs = pdfFonts.pdfMake.vfs;
pdfMake.tableLayouts = {
  Mylayout: {
    fillColor: function (rowIndex, node, columnIndex) {
      return rowIndex % 2 === 0 ? "#CCCCCC" : null;
    },
  },
};

const StoneSearchHistory = () => {
  const [searchHistory, setSearchHistory] = useState([]);
  const [queryFrom, setQueryFrom] = useState([]);
  const [queryFor, setQueryFor] = useState([]);
  const [userQueryFromOption, setUserQueryFromOption] = useState([]);
  const [userQueryForOption, setUserQueryForOption] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [salesListLoader, setSalesListLoader] = useState(false);
  const [access, setAccess] = useState(false);

  let date = new Date(); //Current Date and Time

  useEffect(() => {
    PageTitle("REPORT_STONESEARCHHISTORY");
    checkPermission();
    getQueryFrom();
    getQueryFor();
  }, []);

  const checkPermission = () => {
    let allowed = Constants.USER_PERMISSION.find(
      (iPermission) =>
        iPermission.permissionid ===
        Constants.PERMISSIONS.REPORT_STONESEARCHHISTORY
    ) !== undefined
    if (!allowed) {
      setAccess(true)
      return <NoPermission />
    }
    setFromDate(new Date(date.setDate(date.getDate() - 1)));
    setToDate(new Date());
  }

  const getQueryFrom = () => {
    SavedsearchhistoryProxy.getQueryFromData(
      (response) => {
        setQueryFromData(response);
      }
    );
  };

  const getQueryFor = () => {
    SavedsearchhistoryProxy.getQueryForData(
      (response) => {
        setQueryForData(response);
      }
    );
  };

  const setQueryFromData = (response) => {
    let optionsArray = [];
    optionsArray.push({ value: "", label: "-- ANY --" });
    response.map((data) => {
      let queryFromObj = {
        value: data.id,
        label: data.username,
      };
      optionsArray.push(queryFromObj);
      return true;
    });
    setUserQueryFromOption(optionsArray);
    setQueryFrom(optionsArray.length > 0 ? optionsArray[0] : queryFrom)

  };

  const setQueryForData = (response) => {
    let optionsArray = [];
    optionsArray.push({ value: "", label: "-- ANY --" });
    response.map((data) => {
      let queryFromObj = {
        value: data.id,
        label: data.username,
      };
      optionsArray.push(queryFromObj);
      return true;
    });
    setUserQueryForOption(optionsArray);
    setQueryFor(optionsArray.length > 0 ? optionsArray[0] : queryFor)
  };

  const fullOptionToValue = (selectedData) => {
    if (selectedData.value === "") {
      return [];
    }
    let newArr = [];
    selectedData.map((option) => {
      if (option.value === undefined) {
        newArr.push(option);
      } else {
        newArr.push(option.value);
      }
      return true;
    });
    return newArr.includes("") ? [] : newArr;
  };

  const handleSearch = (queryFrom, queryFor, fromDate, toDate) => {
    setSalesListLoader(true);
    let data = {
      queryfrom: fullOptionToValue(queryFrom),
      queryfor: fullOptionToValue(queryFor),
      datefrom: Utility.ConvertLocalTimeToUtc(dayjs(fromDate).format('YYYY-MM-DD') + "T00:00:00.000"),
      dateto: Utility.ConvertLocalTimeToUtc(dayjs(toDate).format('YYYY-MM-DD') + "T23:59:59.999"),
    };
    SavedsearchhistoryProxy.getSearchHistory(data, (response) => {
      if (response) {
        const historyList = response.map((record, idx) => ({ ...record, id: idx }));
        setSearchHistory(historyList);
        setSalesListLoader(false);
      } else {
        setSalesListLoader(false);
      }
    });
  };

  const columnName = [
    {
      headerName: "SEARCH DATE",
      field: "querydt",
      align: "center",
      width: 130,
      headerAlign: "center",
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.row.querydt),
    },
    {
      headerName: "QUERY FOR",
      field: "queryforusername",
      align: "center",
      headerAlign: "center",
      width: 100,
      renderCell: renderCellExpand,
    },
    {
      headerName: "QUERY FROM",
      width: 100,
      field: "queryfromusername",
      align: "center",
      headerAlign: "center",
      renderCell: renderCellExpand,
    },
    {
      headerName: "BGM",
      field: "bgm",
      headerAlign: "center",
      align: "center",
      width: 100,
      renderCell: renderCellExpand,
    },
    {
      headerName: "BLACK",
      field: "black",
      headerAlign: "center",
      align: "center",
      width: 100,
    },
    {
      headerName: "CLARITY",
      field: "clarity",
      headerAlign: "center",
      width: 75,
      align: "center",
    },
    {
      headerName: "COLOR",
      field: "color",
      headerAlign: "center",
      width: 65,
      align: "center",
    },
    {
      headerName: "C PRICE FROM",
      field: "cpricefrom",
      headerAlign: "center",
      width: 100,
      align: "center",
    },
    {
      headerName: "C PRICE TO",
      field: "cpriceto",
      headerAlign: "center",
      width: 100,
      align: "center",
    },
    {
      headerName: "CUT",
      field: "cut",
      headerAlign: "center",
      align: "center",
      width: 50,
    },
    {
      headerName: "DEPTH FROM",
      field: "depthfrom",
      headerAlign: "center",
      align: "center",
      width: 100,
    },
    {
      headerName: "DEPTH TO",
      field: "depthto",
      headerAlign: "center",
      width: 100,
      align: "center",
    },
    {
      headerName: "F.COL 1",
      field: "fancycolor1",
      width: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      headerName: "F.COL 2",
      field: "fancycolor2",
      width: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      headerName: "F.COL.INTENSITY",
      field: "fancycolorintensity",
      width: 140,
      headerAlign: "center",
      align: "center",
    },
    {
      headerName: "OVERTONE",
      field: "fancycolorovertone",
      headerAlign: "center",
      width: 100,
      align: "center",
    },
    {
      headerName: "EXCHANGE RATE",
      field: "exchangerate",
      width: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      headerName: "FLO",
      width: 60,
      field: "flo",
      headerAlign: "center",
      align: "center",
    },
    {
      headerName: "SIZE",
      field: "generalsize",
      width: 40,
      headerAlign: "center",
      align: "center",
    },
    {
      headerName: "LAB",
      field: "lab",
      width: 100,
      headerAlign: "center",
      align: "center",
    },
    {
      headerName: "LENGTH FROM",
      field: "lengthfrom",
      headerAlign: "center",
      align: "center",
      width: 100,
    },
    {
      headerName: "LENGTH TO",
      field: "lengthto",
      headerAlign: "center",
      align: "center",
      width: 100,
    },
    {
      headerName: "LOCATION ID",
      field: "locationid",
      headerAlign: "center",
      width: 100,
      align: "center",
    },
    {
      headerName: "POL",
      field: "pol",
      width: 40,
      headerAlign: "center",
      align: "center",
    },
    {
      headerName: "RATIO  FROM",
      field: "ratiofrom",
      align: "center",
      headerAlign: "center",
      width: 100,
    },
    {
      headerName: "RATIO TO",
      width: 100,
      field: "ratioto",
      align: "center",
      headerAlign: "center",
    },
    {
      headerName: "REPORT NUMBER",
      field: "reportnumber",
      align: "center",
      width: 100,
      headerAlign: "center",
    },
    {
      headerName: "SHAPE",
      field: "shape",
      align: "center",
      headerAlign: "center",
      width: 80,
    },
    {
      headerName: "SIZE MASTER ID",
      field: "sizemasterid",
      align: "center",
      headerAlign: "center",
      width: 200,
    },
    {
      headerName: "STATUS",
      field: "status",
      align: "center",
      headerAlign: "center",
      width: 100,
    },
    {
      headerName: "SYM",
      field: "sym",
      width: 40,
      align: "center",
      headerAlign: "center",
    },

    {
      headerName: "TABLE FROM",
      field: "tablefrom",
      width: 100,
      align: "center",
      headerAlign: "center",
    },

    {
      headerName: "TABLE TO",
      field: "tableto",
      width: 100,
      align: "center",
      headerAlign: "center",
    },

    {
      headerName: "T PRICE FROM",
      field: "tpricefrom",
      width: 100,
      align: "center",
      headerAlign: "center",
    },

    {
      headerName: "T PRICE TO",
      field: "tpriceto",
      width: 100,
      align: "center",
      headerAlign: "center",
    },

    {
      headerName: "WIDTH FROM",
      field: "widthfrom",
      align: "center",
      headerAlign: "center",
      width: 100,
    },
    {
      headerName: "WIDTH TO",
      field: "widthto",
      align: "center",
      width: 100,
      headerAlign: "center",
    },
  ];

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          alignItems: "center",
          marginHorizontal: "5px",
          "&.MuiDataGrid-toolbarContainer": {
            padding: "0px 4px !important",
          },
        }}
      >
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarExport />
        <Box sx={{ flexGrow: 1, display: "block" }} />
        {salesListLoader ? (
          <CircularProgress size="1rem" />
        ) : (
          <Tooltip title="Reload List">
            <RefreshOutlinedIcon
              onClick={() => handleSearch(queryFrom, queryFor, fromDate, toDate)}
              color="primary"
              fontSize="medium"
              sx={{ cursor: "pointer" }}
            />
          </Tooltip>
        )}
        <GridToolbarQuickFilter debounceMs={500} />
      </GridToolbarContainer>
    )
  }


  return (
    <div>
      <CommonSnackbar
        show={errorSnackbar}
        message={Constants.APiFailErrorMessage}
        variant={"error"}
        autoHide={Constants.SnackBarCloseTimer}
        title={"Failed"}
        handleClose={() => setErrorSnackbar(false)}
      />

      {
        access ? <NoPermission /> : (
          <Box px={1}>
            <Box
              sx={{
                display: "flex",
                width: "100%",
                my: 1,
                gap: 2,
                flexWrap: "wrap",
                flexDirection: "row",
              }}
            >
              {/* Query From */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                }}
              >
                <Typography alignSelf="center" mt={{ xs: 0, sm: 1 }}>
                  Query From
                </Typography>
                <Box sx={{ flex: 1, minWidth: "200px", maxWidth: "220px", }}>
                  <Select
                    menuPlacement="auto"
                    blurInputOnSelect={false}
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    closeMenuOnSelect={false}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    isMulti
                    options={userQueryFromOption}
                    value={queryFrom}
                    onChange={setQueryFrom}
                  />
                </Box>
              </Box>

              {/* Query For */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                }}
              >
                <Typography alignSelf="center" mt={{ xs: 0, sm: 1 }}>
                  Query For
                </Typography>
                <Box sx={{ flex: 1, minWidth: "200px", maxWidth: "220px", }}>
                  <Select
                    menuPlacement="auto"
                    blurInputOnSelect={false}
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    closeMenuOnSelect={false}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    isMulti
                    options={userQueryForOption}
                    value={queryFor}
                    onChange={setQueryFor}
                  />
                </Box>
              </Box>

              {/* From Date */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: "200px",
                }}
              >
                <Typography mt={1}>From</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    className="picker"
                    ampm
                    renderInput={(props) => (
                      <TextField
                        fullWidth
                        size="small"
                        {...props}
                        inputProps={{ ...props.inputProps, readOnly: true }}
                      />
                    )}
                    value={fromDate}
                    inputFormat="DD/MM/YYYY"
                    onChange={(newValue) => {
                      const now = dayjs();
                      setFromDate(dayjs(newValue).set({
                        hour: now.hour(),
                        minute: now.minute(),
                        second: now.second(),
                        millisecond: now.millisecond(),
                      }).toDate());
                    }}
                  />
                </LocalizationProvider>
              </Box>

              {/* To Date */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: "200px",
                }}
              >
                <Typography mt={1}>To</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    className="picker"
                    ampm
                    renderInput={(props) => (
                      <TextField
                        fullWidth
                        size="small"
                        {...props}
                        inputProps={{ ...props.inputProps, readOnly: true }}
                      />
                    )}
                    value={toDate}
                    inputFormat="DD/MM/YYYY"
                    onChange={(newValue) => {
                      const now = dayjs();
                      setToDate(dayjs(newValue).set({
                        hour: now.hour(),
                        minute: now.minute(),
                        second: now.second(),
                        millisecond: now.millisecond(),
                      }).toDate());
                    }}
                  />
                </LocalizationProvider>
              </Box>

              {/* Search Button */}
              <Box
                sx={{
                  alignSelf: "center",
                }}
              >
                <LoadingButton
                  loading={salesListLoader}
                  loadingPosition="start"
                  disabled={queryFrom.length === 0 && queryFor.length === 0}
                  variant="contained"
                  onClick={() => handleSearch(queryFrom, queryFor, fromDate, toDate)}
                  startIcon={<SearchOutlinedIcon size="large" />}
                >
                  Search
                </LoadingButton>
              </Box>
            </Box>
            <Box
              sx={{
                backgroundColor: '#fff',
                color: '#000',
                width: "100%",
                position: "relative",
                "& .MuiTablePagination-selectLabel": {
                  marginTop: "15px",
                },
                "& .MuiTablePagination-displayedRows": {
                  marginTop: "15px",
                },
                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                  fontWeight: Constants.defaultFontWeight,
                },
                "& .css-cc8tf1": {
                  fontWeight: Constants.defaultFontWeight,
                },
                height: '80vh'
              }}
            >
              <DataGridPro
                disableRowSelectionOnClick={true}
                sx={{
                  '& .MuiDataGrid-columnHeader': {
                    color: '#fff',
                    backgroundColor: 'grey',
                  },
                  "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                    py: "4px",
                  },
                  "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                    py: "12px",
                  },
                  "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                    py: "18px",
                  },

                  fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight
                }}
                density="compact"
                slots={{ toolbar: CustomToolbar }}
                rows={searchHistory}
                columns={columnName}
                getRowId={(row) => row.id}
                loading={salesListLoader}
                initialState={{
                  columns: {
                    columnVisibilityModel: {
                      // Hide columns id, the other columns will remain visibl
                    },
                  },
                  sorting: {
                    sortModel: [{ field: "updatedat", sort: "desc" }],
                  },
                  pagination: {
                    paginationModel: Constants.pageSize,
                  },
                }}
                pageSizeOptions={Constants.pageSizeOptions}
                pagination
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
              />
            </Box>
          </Box>
        )}
    </div>)
}

export default StoneSearchHistory;